import { useCallback, useState } from "react";

// localStorage-backed state for a lab, namespaced by prefix so labs sharing an origin
// don't read each other's settings. FrenchNounArticles and GermanNounArticles keep their
// own copies because they also export the raw read/write helpers.

// Safari private mode throws on localStorage access rather than returning null, and a
// blocked-storage browser shouldn't cost us the page. Losing a setting is fine.
export function readStored(prefix, key, fallback) {
  try {
    const stored = window.localStorage.getItem(prefix + key);
    return stored === null ? fallback : JSON.parse(stored);
  } catch (e) {
    return fallback;
  }
}

export function writeStored(prefix, key, value) {
  try {
    window.localStorage.setItem(prefix + key, JSON.stringify(value));
  } catch (e) {
    // Out of quota or blocked - the in-memory value still works for this visit.
  }
}

export default function useLocalStorage(prefix, key, fallback) {
  const [value, setValue] = useState(() => readStored(prefix, key, fallback));

  const store = useCallback(
    (next) => {
      setValue((previous) => {
        const resolved = typeof next === "function" ? next(previous) : next;
        writeStored(prefix, key, resolved);
        return resolved;
      });
    },
    [prefix, key]
  );

  return [value, store];
}
